import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  API, normalizeAuPhone, formatAgent,
} from './domainUtils';
import { utcHourToLocalHour } from '../../../lib/timezone';
import { GradingBanner } from './calls/GradingBanner';
import { CallsToolbar } from './calls/CallsToolbar';
import { CallsTable } from './calls/CallsTable';

export const DomainCallsTab = ({ domainId, token, initialStatusFilter = '', agentFilter = '', criterionFilter = '', timeWindow = null }) => {
  const [calls, setCalls] = useState([]);
  const [gradings, setGradings] = useState({});
  const [vfCounts, setVfCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [grading, setGrading] = useState(false);
  const [statusFilter, setStatusFilter] = useState(initialStatusFilter);
  const [gradingStatus, setGradingStatus] = useState(null);
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState('call_date');
  const [sortDir, setSortDir] = useState('desc');
  const [governanceOnly, setGovernanceOnly] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const loadCalls = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ domain_id: domainId, limit: 200 });
      if (statusFilter) params.set('status', statusFilter);
      if (agentFilter) params.set('agent_id', agentFilter);
      if (criterionFilter) params.set('failing_criterion', criterionFilter);
      if (timeWindow) {
        // New (local-hour) payload from CallVolumeHeatmap: expand to UTC
        // hour + weekday sets so the overnight (9 PM–6 AM) band works.
        if (Array.isArray(timeWindow.localHours) && timeWindow.weekday !== undefined) {
          const offset = ((utcHourToLocalHour(0) ?? 0) + 24) % 24;
          const utcHours = new Set();
          const utcWeekdays = new Set();
          timeWindow.localHours.forEach((lh) => {
            const total = lh - offset;
            const uh = ((total % 24) + 24) % 24;
            const dayShift = Math.floor(total / 24); // -1 when local hour wraps back to previous UTC day
            const uwd = ((timeWindow.weekday + dayShift) % 7 + 7) % 7;
            utcHours.add(uh);
            utcWeekdays.add(uwd);
          });
          params.set('hours', [...utcHours].join(','));
          params.set('weekdays', [...utcWeekdays].join(','));
        } else {
          // Legacy contiguous range payload — still supported for older callers.
          if (timeWindow.weekday !== undefined) params.set('weekday', timeWindow.weekday);
          if (timeWindow.hourMin !== undefined) params.set('hour_min', timeWindow.hourMin);
          if (timeWindow.hourMax !== undefined) params.set('hour_max', timeWindow.hourMax);
        }
      }
      const r = await axios.get(`${API}/calls?${params}`, { headers });
      setCalls(r.data);
      const gradeable = r.data.filter(c => c.status === 'analyzed' || c.status === 'escalated').slice(0, 30);
      const results = await Promise.allSettled(
        gradeable.map(c => axios.get(`${API}/calls/${c.call_id}/grading`, { headers }))
      );
      const map = {};
      results.forEach((res, i) => {
        if (res.status === 'fulfilled') map[gradeable[i].call_id] = res.value.data.overall_score;
      });
      setGradings(map);

      // Fetch VoiceFabric action counts in one bulk trip so we can render
      // the "N actions" chip on rows that had rules fire. Non-blocking —
      // if it fails the chip is simply hidden, table still renders.
      try {
        const ids = r.data.map(c => c.call_id).filter(Boolean);
        if (ids.length) {
          const vfRes = await axios.post(
            `${API}/calls/voicefabric-counts`,
            { call_ids: ids },
            { headers },
          );
          setVfCounts(vfRes.data?.counts || {});
        } else {
          setVfCounts({});
        }
      } catch {
        setVfCounts({});
      }
    } catch (err) {
      toast.error('Failed to load calls');
      console.error('loadCalls error:', err);
    } finally {
      setLoading(false);
    }
  }, [domainId, statusFilter, agentFilter, criterionFilter, timeWindow, headers]);

  useEffect(() => { loadCalls(); }, [loadCalls]);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const r = await axios.get(`${API}/admin/domains/${domainId}/grading-status`, { headers });
        setGradingStatus(r.data);
      } catch (err) {
        console.error('Failed to fetch grading status:', err);
      }
    };
    fetchStatus();
    const interval = setInterval(async () => {
      const r = await axios.get(`${API}/admin/domains/${domainId}/grading-status`, { headers }).catch(() => null);
      if (!r) return;
      setGradingStatus(prev => {
        if (r.data.gradeable === 0 && prev?.gradeable > 0) loadCalls();
        return r.data;
      });
    }, 15000);
    return () => clearInterval(interval);
  }, [domainId, headers]);  

  const pendingCount = calls.filter(c => c.status === 'pending').length;

  const handleGradePending = async () => {
    if (!window.confirm(`Fetch transcripts and AI-grade ${pendingCount} pending calls?`)) return;
    setGrading(true);
    try {
      const r = await axios.post(`${API}/admin/domains/${domainId}/grade-pending`, {}, { headers });
      toast.success(r.data.pending === 0 ? r.data.message : `${r.data.message} Check back in ~2 mins.`);
    } catch (err) {
      toast.error('Failed to start grading');
      console.error('Grade pending error:', err);
    } finally {
      setGrading(false);
    }
  };

  const handleRegradeAll = async () => {
    if (!window.confirm('Re-grade all calls using updated knowledge base documents? Runs in background.')) return;
    setGrading(true);
    try {
      const r = await axios.post(`${API}/admin/domains/${domainId}/regrade-all`, {}, { headers });
      toast.success(r.data.message);
    } catch (err) {
      toast.error('Failed to start re-grade');
      console.error('Regrade error:', err);
    } finally {
      setGrading(false);
    }
  };

  const handleSort = (field) => {
    setSortField(f => {
      if (f === field) { setSortDir(d => d === 'asc' ? 'desc' : 'asc'); return f; }
      setSortDir('asc');
      return field;
    });
  };

  const filtered = useMemo(() => {
    let list = [...calls];
    if (governanceOnly) {
      list = list.filter(c => c.governance_breach);
    }
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(c =>
        formatAgent(c.agent_id, c.agent_name, c.extension).toLowerCase().includes(q) ||
        normalizeAuPhone(c.customer_phone).includes(q) ||
        (c.call_id || '').toLowerCase().includes(q) ||
        (c.call_type || '').includes(q)
      );
    }
    list.sort((a, b) => {
      let av;
      let bv;
      if (sortField === 'score') {
        av = gradings[a.call_id] ?? -1;
        bv = gradings[b.call_id] ?? -1;
      } else if (sortField === 'call_date') {
        av = new Date(a.call_date);
        bv = new Date(b.call_date);
      } else if (sortField === 'duration_seconds') {
        av = a.duration_seconds;
        bv = b.duration_seconds;
      } else {
        av = String(a[sortField] ?? '').toLowerCase();
        bv = String(b[sortField] ?? '').toLowerCase();
      }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [calls, search, sortField, sortDir, gradings, governanceOnly]);

  const governanceCount = useMemo(
    () => calls.filter(c => c.governance_breach).length,
    [calls],
  );

  return (
    <div className="space-y-3">
      {criterionFilter && (
        <div className="flex items-center justify-between bg-rose-500/10 border border-rose-500/20 rounded-xl px-5 py-3" data-testid="criterion-filter-banner">
          <div>
            <p className="text-rose-300 text-sm font-medium">
              Showing calls where criterion failed: <span className="text-white font-semibold">&ldquo;{criterionFilter}&rdquo;</span>
            </p>
            <p className="text-rose-300/50 text-xs">{calls.length} matching call{calls.length === 1 ? '' : 's'} · click &ldquo;Clear filter&rdquo; to remove</p>
          </div>
        </div>
      )}

      {timeWindow && (
        <div className="flex items-center justify-between bg-teal-500/10 border border-teal-500/20 rounded-xl px-5 py-3" data-testid="timewindow-filter-banner">
          <div>
            <p className="text-teal-300 text-sm font-medium">
              Showing calls during: <span className="text-white font-semibold">{timeWindow.label || 'selected window'}</span>
            </p>
            <p className="text-teal-300/50 text-xs">{calls.length} matching call{calls.length === 1 ? '' : 's'}</p>
          </div>
        </div>
      )}

      {pendingCount > 0 && !agentFilter && (
        <GradingBanner
          pendingCount={pendingCount}
          gradingStatus={gradingStatus}
          grading={grading}
          onGradePending={handleGradePending}
          onRetryFailed={async () => {
            try {
              const r = await axios.post(
                `${API}/admin/domains/${domainId}/retry-failed-gradings`,
                {}, { headers: { Authorization: `Bearer ${token}` } },
              );
              toast.success(r.data.message || 'Retry queued');
            } catch (err) {
              toast.error(err?.response?.data?.detail || 'Retry failed to start');
            }
          }}
        />
      )}

      <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
        <CallsToolbar
          search={search} onSearch={setSearch}
          statusFilter={statusFilter} onStatusFilter={setStatusFilter}
          grading={grading} onRegradeAll={handleRegradeAll}
          governanceOnly={governanceOnly} onToggleGovernance={setGovernanceOnly}
          governanceCount={governanceCount}
        />

        <p className="text-white/20 text-xs px-4 py-2">{filtered.length} of {calls.length} call{calls.length !== 1 ? 's' : ''}</p>

        <CallsTable
          loading={loading}
          calls={filtered}
          gradings={gradings}
          vfCounts={vfCounts}
          sortField={sortField}
          sortDir={sortDir}
          onSort={handleSort}
        />
      </div>
    </div>
  );
};
